import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { isNotNil } from 'utils/object-util'
import Document from 'documents/Document'
import { DocumentActionsAdapter } from 'documents/DocumentActionsAdapter'

const SPREADSHEET_TYPES = [
  'application/vnd.ms-excel',
  'application/msexcel',
  'application/x-msexcel',
  'application/x-ms-excel',
  'application/x-excel',
  'application/x-dos_ms_excel',
  'application/xls',
  'application/x-xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
]

type PreviewKind = 'PDF' | 'Image' | 'SpreadSheet' | 'Unavailable'

export interface DocumentBlob {
  url: string
  size: number
  mediaType: string
}

export default class WorkpapersPreviewerViewModel {
  blob: DocumentBlob | null = null

  constructor(
    private document: Document,
    private adapter: DocumentActionsAdapter,
  ) {
    makeObservable(this, {
      blob: observable,
      previewKind: computed,
      download: action.bound,
    })
  }

  get previewKind(): PreviewKind {
    if (this.blob?.mediaType.startsWith('image')) {
      return 'Image'
    }

    if (this.blob?.mediaType.endsWith('pdf')) {
      return 'PDF'
    }

    if (SPREADSHEET_TYPES.includes(this.blob?.mediaType || '')) {
      return 'SpreadSheet'
    }

    return 'Unavailable'
  }

  downloadBlob = task.resolved(async () => {
    if (this.blob || !isNotNil(this.document.latestVersion)) {
      return
    }

    const blob = await this.adapter.downloadBlob(this.document)

    if (blob) {
      this.blob = {
        url: URL.createObjectURL(blob),
        size: blob.size,
        mediaType: blob.type,
      }
    }
  })

  download() {
    this.adapter.downloadDocument(this.document)
  }

  activate() {
    this.downloadBlob().then()
  }

  deactivate() {
    if (this.blob) {
      URL.revokeObjectURL(this.blob.url)
    }

    this.blob = null
  }
}
